import { AsyncPipe, NgIf } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-enterprise';
import { finalize, switchMap, from, concatMap, toArray } from 'rxjs';
import { TsPipesModule } from 'tsui/lib/cross-cutting/pipes/ts-pipes.module';
import { TSIconModule } from 'tsui/lib/ts-icon/ts-icon.module';
import { TsButtonModule } from 'tsui/lib/ts-button/ts-button.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FoldersTabService } from '@shared/modules/folders/folders.service';
import { FileResource } from '@shared/modules/folders/folders.types';
import { FormFieldUtils } from '@shared/Utils/form-field.utils';

export type FileCellEditorParams = ICellEditorParams & {
  multiSelect: boolean;
  recordType: string;
  allowedTypes?: string[];
};

@Component({
  standalone: true,
  imports: [
    AsyncPipe,
    NgIf,
    MatProgressSpinnerModule,
    TsPipesModule,
    FormsModule,
    ReactiveFormsModule,
    TSIconModule,
    TsButtonModule,
    MatTooltipModule,
  ],
  template: `
    <div class="container" [style]="{ width: width }">
      <div class="file-upload-container">
        <div class="flex flex-row justify-between items-center w-full" *ngIf="!uploadingFile">
          <span class="file-label" *ngIf="!selectedFileName">Select a file...</span>
          <span class="file-name" *ngIf="selectedFileName">{{ selectedFileName }}</span>

          <div class="actions">
            <ts-button
              class="upload-btn"
              type="icon"
              htmlButtonType="button"
              (buttonClick)="fileInput.click()"
              [disabled]="uploadingFile"
            >
              <ts-icon icon="ts_upload-alt"></ts-icon>
              <input
                style="display: none"
                type="file"
                (change)="onFileChanged($event)"
                #fileInput
                [accept]="accept"
                [multiple]="params?.multiSelect"
              />
            </ts-button>
          </div>
        </div>

        <div *ngIf="uploadingFile" class="loading-container">
          <mat-progress-spinner class="spinner" diameter="24" mode="indeterminate"></mat-progress-spinner>
          <span>Uploading...</span>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .container {
        background: white;
        padding: 12px;
        min-height: 60px;
      }

      .file-upload-container {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .file-label {
        color: #888;
      }

      .file-name {
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 70%;
      }

      .actions {
        display: flex;
        align-items: center;
      }

      .loading-container {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }
    `,
  ],
})
export class TableFileUploadEditorComponent implements ICellEditorAngularComp {
  @ViewChild('fileInput') fileInput: ElementRef;

  params: FileCellEditorParams;
  width = 'auto';
  uploadingFile = false;
  selectedFileName: string = null;
  selectedFileResource: FileResource = null;
  selectedFileResources: FileResource[] = [];
  accept: string;

  constructor(private foldersTabService: FoldersTabService) {}

  agInit(params: FileCellEditorParams): void {
    this.params = params;
    this.width = params.column.getActualWidth() + 'px';

    // If there's an existing value, extract the file name(s)
    if (params.value) {
      if (Array.isArray(params.value)) {
        this.selectedFileName = `${params.value.length} file(s) selected`;
      } else if (typeof params.value === 'string' && params.value.includes('|')) {
        this.selectedFileName = params.value.substring(params.value.indexOf('|') + 1);
      }
    }
    this.accept = this.computeAccept(this.params.allowedTypes || ['any']);
  }

  getValue(): any {
    if (this.params?.multiSelect) {
      // When multi-select, merge existing values with newly uploaded ones (append, do not overwrite)
      const existingTokens = this.parseExistingTokens(this.params?.value);
      const newTokens = Array.isArray(this.selectedFileResources)
        ? this.selectedFileResources.map((fr) => `${fr.id}|${fr.name}`)
        : [];

      if (!newTokens.length) {
        // No new uploads; keep original as-is
        return this.params.value;
      }

      const merged = this.mergeTokens(existingTokens, newTokens);
      return merged;
    } else {
      if (!this.selectedFileResource) {
        return this.params.value; // Return original value if no new file was uploaded
      }
      return `${this.selectedFileResource.id}|${this.selectedFileResource.name}`;
    }
  }

  isPopup(): boolean {
    return true;
  }

  getPopupPosition(): 'over' | 'under' | undefined {
    return 'under';
  }

  onFileChanged(event: any): void {
    const files: File[] = this.params?.multiSelect
      ? Array.from(event.target.files || [])
      : ([event.target.files?.[0]].filter(Boolean) as File[]);
    if (!files || files.length === 0) return;

    this.uploadingFile = true;
    this.selectedFileName = this.params?.multiSelect ? `${files.length} file(s) selected` : files[0].name;

    // Get folder ID from context
    const folderId = this.params.node.data?.root_folder_resource_id;
    if (!folderId) {
      this.uploadingFile = false;
      return;
    }

    if (this.params?.multiSelect) {
      from(files)
        .pipe(
          concatMap((file) =>
            this.foldersTabService
              .createFile(folderId, { folder_resource_id: folderId, name: file.name })
              .pipe(switchMap((fr) => this.foldersTabService.uploadDocument(folderId, fr.id, file))),
          ),
          toArray(),
          finalize(() => (this.uploadingFile = false)),
        )
        .subscribe((uploaded: FileResource[]) => {
          this.selectedFileResources = uploaded || [];
          // Update label to reflect total files (existing + newly uploaded)
          const existingCount = this.parseExistingTokens(this.params?.value).length;
          const newCount = this.selectedFileResources.length;
          this.selectedFileName = `${existingCount + newCount} file(s) selected`;
          this.params.stopEditing();
        });
    } else {
      const file = files[0];
      const draft = { folder_resource_id: folderId, name: file.name };
      this.foldersTabService
        .createFile(folderId, draft)
        .pipe(
          switchMap((fr) => this.foldersTabService.uploadDocument(folderId, fr.id, file)),
          finalize(() => (this.uploadingFile = false)),
        )
        .subscribe((fr) => {
          this.selectedFileResource = fr;
          this.params.stopEditing();
        });
    }
  }

  private computeAccept(allowedTypes: string[]): string {
    return FormFieldUtils.getInputAcceptFileAttribute(allowedTypes as FormFieldUtils.AllowedRecordFormats[]);
  }

  // Utilities for merging existing and new file tokens in multi-select mode
  private parseExistingTokens(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return (value as string[]).filter((v) => typeof v === 'string' && v.includes('|'));
    }
    if (typeof value === 'string' && value.includes('|')) {
      return [value];
    }
    return [];
  }

  private mergeTokens(existing: string[], incoming: string[]): string[] {
    if (!existing?.length) return [...incoming];
    if (!incoming?.length) return [...existing];

    // De-duplicate by id, prefer the incoming token in case of conflict
    const byId = new Map<number, string>();

    const put = (token: string, prefer = false) => {
      const pipeIndex = token.indexOf('|');
      const id = Number(token.substring(0, pipeIndex));
      if (!byId.has(id) || prefer) {
        byId.set(id, token);
      }
    };

    existing.forEach((t) => put(t, false));
    incoming.forEach((t) => put(t, true));

    return Array.from(byId.values());
  }
}
